import React, { useEffect, useMemo } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { cn } from '@/lib/cn';

const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            }
          }
        }
      }
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run()
    };
  }
});

const fontSizes = ['10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];
const fontFamilies = [
  { label: 'Default', value: '' },
  { label: 'Arial', value: 'Arial' },
  { label: 'Times New Roman', value: 'Times New Roman' },
  { label: 'Calibri', value: 'Calibri' },
  { label: 'Georgia', value: 'Georgia' }
];

const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  className,
  contentClassName,
  readOnly = false
}) => {
  const extensions = useMemo(
    () => [
      StarterKit,
      Underline,
      TextStyle,
      FontFamily,
      FontSize,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell
    ],
    []
  );

  const editor = useEditor({
    extensions,
    content: value || '',
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: cn('ProseMirror focus:outline-none min-h-[12rem] px-3 py-3', contentClassName)
      }
    },
    onUpdate: ({ editor: ed }) => {
      if (typeof onChange === 'function') onChange(ed.getHTML());
    }
  });

  useEffect(() => {
    if (!editor) return;
    const next = value || '';
    if (editor.getHTML() === next) return;
    editor.commands.setContent(next, false);
  }, [editor, value]);

  if (!editor) {
    return (
      <div className={cn('border border-gray-200 rounded-lg bg-white', className)}>
        <div className="p-3 text-sm text-gray-500">{placeholder || 'Loading editor...'}</div>
      </div>
    );
  }

  const currentFontSize = editor.getAttributes('textStyle')?.fontSize || '';
  const currentFontFamily = editor.getAttributes('textStyle')?.fontFamily || '';
  const canUndo = editor.can().chain().focus().undo().run();
  const canRedo = editor.can().chain().focus().redo().run();

  return (
    <div className={cn('border border-gray-200 rounded-lg bg-white flex flex-col overflow-hidden', className)}>
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2 px-2 py-2 border-b border-gray-200 bg-gray-50">
          <select
            value={currentFontFamily}
            onChange={(e) => {
              const v = e.target.value;
              if (v) editor.chain().focus().setFontFamily(v).run();
              else editor.chain().focus().unsetFontFamily().run();
            }}
            className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
          >
            {fontFamilies.map((f) => (
              <option key={f.label} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>

          <select
            value={currentFontSize}
            onChange={(e) => {
              const v = e.target.value;
              if (v) editor.chain().focus().setFontSize(v).run();
              else editor.chain().focus().unsetFontSize().run();
            }}
            className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
          >
            <option value="">Size</option>
            {fontSizes.map((s) => (
              <option key={s} value={s}>
                {s.replace('px', '')}
              </option>
            ))}
          </select>

          <button
            type="button"
            disabled={!canUndo}
            onClick={() => editor.chain().focus().undo().run()}
            className={cn(
              'h-9 px-2 rounded-md border border-gray-200 bg-white text-sm',
              !canUndo && 'opacity-50 cursor-not-allowed'
            )}
          >
            Undo
          </button>
          <button
            type="button"
            disabled={!canRedo}
            onClick={() => editor.chain().focus().redo().run()}
            className={cn(
              'h-9 px-2 rounded-md border border-gray-200 bg-white text-sm',
              !canRedo && 'opacity-50 cursor-not-allowed'
            )}
          >
            Redo
          </button>

          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={cn(
              'h-9 px-2 rounded-md border border-gray-200 bg-white text-sm font-semibold',
              editor.isActive('bold') && 'bg-indigo-50 border-indigo-200 text-indigo-700'
            )}
          >
            B
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={cn(
              'h-9 px-2 rounded-md border border-gray-200 bg-white text-sm italic',
              editor.isActive('italic') && 'bg-indigo-50 border-indigo-200 text-indigo-700'
            )}
          >
            I
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={cn(
              'h-9 px-2 rounded-md border border-gray-200 bg-white text-sm underline',
              editor.isActive('underline') && 'bg-indigo-50 border-indigo-200 text-indigo-700'
            )}
          >
            U
          </button>

          <div className="w-px h-6 bg-gray-200 mx-1" />

          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={cn(
              'h-9 px-2 rounded-md border border-gray-200 bg-white text-sm',
              editor.isActive({ textAlign: 'left' }) && 'bg-indigo-50 border-indigo-200 text-indigo-700'
            )}
          >
            Left
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={cn(
              'h-9 px-2 rounded-md border border-gray-200 bg-white text-sm',
              editor.isActive({ textAlign: 'center' }) && 'bg-indigo-50 border-indigo-200 text-indigo-700'
            )}
          >
            Center
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={cn(
              'h-9 px-2 rounded-md border border-gray-200 bg-white text-sm',
              editor.isActive({ textAlign: 'right' }) && 'bg-indigo-50 border-indigo-200 text-indigo-700'
            )}
          >
            Right
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            className={cn(
              'h-9 px-2 rounded-md border border-gray-200 bg-white text-sm',
              editor.isActive({ textAlign: 'justify' }) && 'bg-indigo-50 border-indigo-200 text-indigo-700'
            )}
          >
            Justify
          </button>

          <div className="w-px h-6 bg-gray-200 mx-1" />

          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={cn(
              'h-9 px-2 rounded-md border border-gray-200 bg-white text-sm',
              editor.isActive('bulletList') && 'bg-indigo-50 border-indigo-200 text-indigo-700'
            )}
          >
            • List
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={cn(
              'h-9 px-2 rounded-md border border-gray-200 bg-white text-sm',
              editor.isActive('orderedList') && 'bg-indigo-50 border-indigo-200 text-indigo-700'
            )}
          >
            1. List
          </button>

          <div className="w-px h-6 bg-gray-200 mx-1" />

          <button
            type="button"
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
            className="h-9 px-2 rounded-md border border-gray-200 bg-white text-sm"
          >
            Insert Table
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeaderRow().run()}
            className="h-9 px-2 rounded-md border border-gray-200 bg-white text-sm"
          >
            Header Row
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeaderColumn().run()}
            className="h-9 px-2 rounded-md border border-gray-200 bg-white text-sm"
          >
            Header Col
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().addRowAfter().run()}
            className="h-9 px-2 rounded-md border border-gray-200 bg-white text-sm"
          >
            +Row
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().addColumnAfter().run()}
            className="h-9 px-2 rounded-md border border-gray-200 bg-white text-sm"
          >
            +Col
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().deleteRow().run()}
            className="h-9 px-2 rounded-md border border-gray-200 bg-white text-sm"
          >
            -Row
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().deleteColumn().run()}
            className="h-9 px-2 rounded-md border border-gray-200 bg-white text-sm"
          >
            -Col
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().mergeCells().run()}
            className="h-9 px-2 rounded-md border border-gray-200 bg-white text-sm"
          >
            Merge
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().splitCell().run()}
            className="h-9 px-2 rounded-md border border-gray-200 bg-white text-sm"
          >
            Split
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().deleteTable().run()}
            className="h-9 px-2 rounded-md border border-gray-200 bg-white text-sm"
          >
            Delete Table
          </button>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default RichTextEditor;
export { RichTextEditor };
